<template>
  <div class="measurement-result-input-table-row">
    <div class="table-cell name">
      <base-select-box
        v-if="laboratoryItemOptions.length"
        data-test="base-select-box"
        :is-value-string="true"
        :select-data="laboratoryItemOptions"
        :styles="{ width: '210px' }"
        :value="measurementResultItem.masterLaboratoryItemCode"
        :value-key="'code'"
        @input="inputValue('masterLaboratoryItemCode', $event)"
      />
      <div v-else class="text" data-test="item-name">
        <span>{{ displayItemName }}</span>
      </div>
    </div>
    <div class="table-cell">{{ displayValue }}</div>
    <div class="table-cell">
      <validation-provider
        v-if="hasUnitAndRange"
        class="validation-provider"
        data-test="provider unit"
        :rules="{ notSurrogatePair: true }"
        v-slot="{ errors }"
      >
        <base-text-box
          data-test="base-text-box unit"
          :maxlength="10"
          :styles="{ width: '140px' }"
          :value="measurementResultItem.unit"
          @input="inputValue('unit', $event)"
        />
        <div class="error" data-test="error unit">
          <span>{{ errors[0] }}</span>
        </div>
      </validation-provider>
    </div>
    <div class="table-cell">
      <validation-observer
        v-if="hasUnitAndRange"
        class="validation-observer"
        data-test="observer range"
        tag="div"
        v-slot="{ errors }"
      >
        <div class="input-wrapper">
          <validation-provider
            class="validation-provider"
            tag="div"
            vid="low"
            :rules="lowRules"
          >
            <base-text-box
              data-test="base-text-box low"
              :maxlength="10"
              :styles="makeLowInputStyle(errors)"
              :value="measurementResultItem.referenceRangeLow"
              @blur="focus('low')"
              @input="inputValue('referenceRangeLow', $event, 'low')"
            />
          </validation-provider>
          <div class="en-dash">–</div>
          <validation-provider
            class="validation-provider"
            tag="div"
            vid="high"
            :rules="highRules"
          >
            <base-text-box
              data-test="base-text-box high"
              :maxlength="10"
              :styles="makeHighInputStyle(errors)"
              :value="measurementResultItem.referenceRangeHigh"
              @blur="focus('high')"
              @input="inputValue('referenceRangeHigh', $event, 'high')"
            />
          </validation-provider>
        </div>
        <div class="error" data-test="error range">
          <span>{{ toErrorText(errors) }}</span>
        </div>
      </validation-observer>
    </div>
  </div>
</template>

<script>
import BaseSelectBox from '@/components/parts/atoms/BaseSelectBox'
import BaseTextBox from '@/components/parts/atoms/BaseTextBox'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { mapGetters } from 'vuex'
import { hasUnitAndRange } from '@/utils/measurement_utils'
import { toDisplayItemName, toDisplayValue } from '@/utils/measurement_utils'

export default {
  name: 'MeasurementResultInputTableRow',

  components: {
    BaseSelectBox,
    BaseTextBox,
    ValidationObserver,
    ValidationProvider
  },

  props: {
    index: { type: Number, default: 0 },
    instrumentCode: { type: String, default: '' },
    measurementResultItem: { type: Object, default: () => ({}) }
  },

  data() {
    return {
      focusedVId: '',
      floatErrorMessage: '0以上の数値を入力してください',
      laboratoryItemOptions: [],
      selectableItemCodeSets: [
        ['L002218', 'L002224'],
        ['L002219', 'L002227', 'L002228', 'L002229']
      ]
    }
  },

  computed: {
    ...mapGetters({
      getMasterLaboratoryItem: 'master/getMasterLaboratoryItemIndexedByCode'
    }),
    displayItemName() {
      const itemCode = this.measurementResultItem.masterLaboratoryItemCode
      const masterLaboratoryItem = this.getMasterLaboratoryItem(itemCode)
      return toDisplayItemName(
        masterLaboratoryItem?.enName,
        this.measurementResultItem
      )
    },
    displayValue() {
      return toDisplayValue(this.instrumentCode, this.measurementResultItem)
    },
    hasUnitAndRange() {
      const itemCode = this.measurementResultItem.masterLaboratoryItemCode
      return hasUnitAndRange(itemCode)
    },
    highRules() {
      const message = this.floatErrorMessage
      const low = this.measurementResultItem.referenceRangeLow
      return this.isFloat(low)
        ? { floatRule: { message }, greaterThan: { minValue: low } }
        : { floatRule: { message } }
    },
    lowRules() {
      const message = this.floatErrorMessage
      const high = this.measurementResultItem.referenceRangeHigh
      return this.isFloat(high)
        ? { floatRule: { message }, lessThan: { maxValue: high } }
        : { floatRule: { message } }
    }
  },

  created() {
    this.setLaboratoryItemOptions()
  },

  methods: {
    focus(vid) {
      this.focusedVId = vid
    },
    inputValue(key, value, vid = '') {
      this.focusedVId = vid
      this.$emit('input', { index: this.index, key, value })
    },
    isFloat(value) {
      const regex = /^([1-9]\d*|0)(\.\d+)?$/
      const isFloat = regex.test(value)
      return isFloat
    },
    makeHighInputStyle(errors) {
      const baseStyle = { textAlign: 'right', width: '116px' }
      return this.showHighError(errors)
        ? { ...baseStyle, borderColor: 'red' }
        : baseStyle
    },
    makeLowInputStyle(errors) {
      const baseStyle = { textAlign: 'right', width: '116px' }
      return this.showLowError(errors)
        ? { ...baseStyle, borderColor: 'red' }
        : baseStyle
    },
    setLaboratoryItemOptions() {
      const masterLaboratoryItems = this.$store.getters[
        'master/getMasterLaboratoryItems'
      ]
      const { masterLaboratoryItemCode } = this.measurementResultItem
      for (const itemCodeSet of this.selectableItemCodeSets) {
        if (itemCodeSet.includes(masterLaboratoryItemCode)) {
          this.laboratoryItemOptions = masterLaboratoryItems
            .filter(v => itemCodeSet.includes(v.code))
            .map(v => ({ code: v.code, name: v.enName }))
          break
        }
      }
    },
    showHighError(errors) {
      const focusedVId = this.focusedVId
      const { high } = errors
      const hasHighError = high?.length > 0
      return hasHighError && focusedVId === 'high'
    },
    showLowError(errors) {
      const focusedVId = this.focusedVId
      const { low } = errors
      const hasLowError = low?.length > 0
      return hasLowError && focusedVId === 'low'
    },
    toErrorText(errors) {
      const focusedVId = this.focusedVId
      const { low, high } = errors
      const hasLowError = low?.length > 0
      const hasHighError = high?.length > 0
      let text = ''
      if (hasLowError && focusedVId === 'low') {
        text = low[0]
      } else if (hasHighError && focusedVId === 'high') {
        text = high[0]
      } else if (hasLowError) {
        text = low[0]
        this.focusedVId = 'low'
      } else if (hasHighError) {
        text = high[0]
        this.focusedVId = 'high'
      }
      return text
    }
  }
}
</script>

<style lang="scss" scoped>
@mixin table-cell {
  padding: 4px 8px;
  border-right: 1px solid #{$light-grey};
  border-bottom: 1px solid #{$light-grey};
  font-size: 14px;
  overflow-wrap: anywhere;
  line-break: strict;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  &.label {
    background-color: #{$very-light-shade-orange};
  }
}

.measurement-result-input-table-row {
  grid-column: span 4;
  display: grid;
  grid-template-columns: subgrid;
  height: 50px;
  > .table-cell {
    @include table-cell;
    text-align: center;
    &.name {
      justify-content: left;
    }
    > .validation-provider {
      position: relative;
      > .error {
        position: absolute;
        z-index: 10;
        color: #{$tomato};
        font-size: 11px;
        text-align: left;
        white-space: nowrap;
        background-color: #{$white};
      }
    }
    > .validation-observer {
      position: relative;
      > .input-wrapper {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 0 10px;
      }
      > .error {
        position: absolute;
        z-index: 10;
        color: #{$tomato};
        font-size: 11px;
        text-align: left;
        white-space: nowrap;
        background-color: #{$white};
      }
    }
  }
}
</style>
